import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Loader2, AlertTriangle, Plus, Facebook, ExternalLink, Search, RotateCcw, Scale, GitBranch } from 'lucide-react';
import type { MetaPage } from '@/pages/Meta/Connection/types/meta.types';
import {
    getUtilityTemplateGroupDetail,
    addPagesToGroup,
    retryUtilityTemplates,
    type UtilityTemplate,
} from '@/services/api/utility-templates';
import UtilityTemplateMessengerPreview from './UtilityTemplateMessengerPreview';
import AddPagesDialog from './AddPagesDialog';
import PageMetaTemplatesCompareDialog from './PageMetaTemplatesCompareDialog';

interface GroupDetailDialogProps {
    groupId: number | null;
    onOpenChange: (open: boolean) => void;
    pages: MetaPage[];
}

const STATUS_LABEL: Record<string, string> = {
    draft: 'Rascunho',
    pending: 'Pendente',
    approved: 'Aprovado',
    rejected: 'Rejeitado',
    failed: 'Falha',
};

const STATUS_BADGE_CLASS: Record<string, string> = {
    draft: 'bg-gray-100 text-gray-600 border-gray-200',
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    approved: 'bg-cyan-50 text-cyan-700 border-cyan-200',
    rejected: 'bg-red-50 text-red-700 border-red-200',
    failed: 'bg-red-50 text-red-700 border-red-200',
};

// Stripe lateral por status — permite escanear a lista por cor sem ler o badge.
const STATUS_STRIPE_CLASS: Record<string, string> = {
    draft: 'border-l-amber-300',
    pending: 'border-l-amber-400',
    approved: 'border-l-emerald-500',
    rejected: 'border-l-red-500',
    failed: 'border-l-red-500',
};

function ErrorState({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <div className="text-center py-12 px-6">
            <AlertTriangle className="mx-auto h-10 w-10 text-amber-400" />
            <h3 className="mt-2 text-lg font-medium text-gray-900">{title}</h3>
            <div className="mt-2 text-sm text-gray-500 space-y-2">{children}</div>
        </div>
    );
}

export default function GroupDetailDialog({ groupId, onOpenChange, pages }: GroupDetailDialogProps) {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const [addPagesDialogOpen, setAddPagesDialogOpen] = useState(false);
    const [selectedFailedIds, setSelectedFailedIds] = useState<Set<number>>(new Set());
    const [comparePageId, setComparePageId] = useState<string | null>(null);

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('all');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [selectedLanguage, setSelectedLanguage] = useState('all');
    // Só páginas com translated_content.retry_origin (ver SubmissionRow,
    // badge "Variante de retry") — filtra pra achar rápido quais páginas
    // desse grupo já passaram por um retry de rejected.
    const [showOnlyVariants, setShowOnlyVariants] = useState(false);
    // 'original' = copy-fonte do grupo (o que sempre foi exibido no preview).
    // Qualquer outro valor é um idioma (ver languageOptions) — nesse caso o
    // preview troca para o translated_content da 1ª submissão daquele
    // idioma que já tenha tradução persistida.
    const [previewLanguage, setPreviewLanguage] = useState('original');

    const {
        data: group,
        isLoading,
        isError,
        error,
    } = useQuery({
        queryKey: ['utility-template-group', groupId],
        queryFn: () => getUtilityTemplateGroupDetail(groupId as number),
        enabled: groupId !== null,
        retry: false, // Não retenta em caso de erro (ex: 404)
    });

    const pageById = useMemo(() => {
        const map = new Map<string, MetaPage>();
        for (const p of pages) map.set(p.page_id, p);
        return map;
    }, [pages]);

    const submittedPageIds = useMemo(
        () => new Set((group?.submissions ?? []).map((s) => s.page_id)),
        [group]
    );

    const categoryOptions = useMemo(() => {
        const unique = new Set<string>();
        (group?.submissions ?? []).forEach((s) => {
            const name = pageById.get(s.page_id)?.internal_category_name;
            if (name) unique.add(name);
        });
        return Array.from(unique).sort().map((c) => ({ value: c, label: c }));
    }, [group, pageById]);

    const languageOptions = useMemo(() => {
        const unique = new Set<string>();
        (group?.submissions ?? []).forEach((s) => unique.add(s.language));
        return Array.from(unique).sort().map((l) => ({ value: l, label: l }));
    }, [group]);

    // Opções do select de preview: "Original (fonte)" sempre primeiro,
    // seguido de cada idioma que tenha ao menos 1 submissão com
    // translated_content já persistido (idiomas sem tradução salva ainda —
    // ex: falha antes de traduzir, ou reprocessamento pendente — não têm o
    // que mostrar de diferente do original, então ficam de fora do select).
    const previewLanguageOptions = useMemo(() => {
        const translatedLangs = new Set(
            (group?.submissions ?? [])
                .filter((s) => s.translated_content?.translated)
                .map((s) => s.language)
        );
        return [
            { value: 'original', label: `Original${group?.source_language ? ` (${group.source_language})` : ''}` },
            ...Array.from(translatedLangs).sort().map((l) => ({ value: l, label: l })),
        ];
    }, [group]);

    // Conteúdo efetivo do preview: original do grupo, ou o translated_content
    // da 1ª submissão daquele idioma que tenha tradução salva.
    const previewContent = useMemo(() => {
        if (previewLanguage === 'original' || !group) {
            return { title: group?.title, subtitle: group?.subtitle, buttons: group?.buttons };
        }
        const submission = (group.submissions ?? []).find(
            (s) => s.language === previewLanguage && s.translated_content?.translated
        );
        const translated = submission?.translated_content?.translated;
        if (!translated) {
            // Não deveria acontecer (previewLanguageOptions só lista idiomas
            // com tradução salva), mas cai no original em vez de tela vazia.
            return { title: group.title, subtitle: group.subtitle, buttons: group.buttons };
        }
        return {
            title: translated.title,
            subtitle: translated.subtitle,
            buttons: translated.buttons?.map((b) => ({ type: 'URL', text: b.text ?? '' })),
        };
    }, [group, previewLanguage]);

    // Submissions filtradas por busca/categoria/idioma/variante, SEM o filtro
    // de status — base tanto para a lista final (com matchesStatus) quanto
    // para as contagens dos KPIs.
    const submissionsBeforeStatusFilter = useMemo(() => {
        return (group?.submissions ?? []).filter((s) => {
            const page = pageById.get(s.page_id);
            const matchesSearch =
                !searchTerm ||
                (page?.page_name ?? '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                s.page_id.includes(searchTerm.trim());
            const matchesCategory =
                selectedCategory === 'all' || page?.internal_category_name === selectedCategory;
            const matchesLanguage = selectedLanguage === 'all' || s.language === selectedLanguage;
            const matchesVariant = !showOnlyVariants || !!s.translated_content?.retry_origin;
            return matchesSearch && matchesCategory && matchesLanguage && matchesVariant;
        });
    }, [group, pageById, searchTerm, selectedCategory, selectedLanguage, showOnlyVariants]);

    // Contagens por status para os KPI tiles. "draft" é agrupado dentro de "pending":
    // visualmente os dois representam "ainda não resolvido" (ver STATUS_LABEL/STATUS_BADGE_CLASS
    // acima, onde draft e pending já usam a mesma paleta âmbar) — expor um 6º tile só para
    // draft poluiria a faixa sem agregar sinal operacional novo.
    const statusCounts = useMemo(() => {
        const counts = { all: 0, approved: 0, pending: 0, rejected: 0, failed: 0 };
        for (const s of submissionsBeforeStatusFilter) {
            counts.all += 1;
            if (s.status === 'approved') counts.approved += 1;
            else if (s.status === 'rejected') counts.rejected += 1;
            else if (s.status === 'failed') counts.failed += 1;
            else counts.pending += 1; // draft + pending
        }
        return counts;
    }, [submissionsBeforeStatusFilter]);

    const filteredSubmissions = useMemo(() => {
        return submissionsBeforeStatusFilter.filter((s) => {
            if (selectedStatus === 'all') return true;
            if (selectedStatus === 'pending') return s.status === 'pending' || s.status === 'draft';
            return s.status === selectedStatus;
        });
    }, [submissionsBeforeStatusFilter, selectedStatus]);

    const handleKpiClick = (status: string) => {
        setSelectedStatus((prev) => (prev === status ? 'all' : status));
    };

    const { mutate: addPages, isPending: adding } = useMutation({
        // force_submit=true sempre: adicionar página pelo modal do grupo é uma
        // intenção explícita de ter aquela página no grupo, mesmo que já exista
        // um template antigo/quebrado para ela (ex: traduzido com o bug do
        // idioma corrigido depois) — sem isso, a submissão inteira falhava
        // silenciosamente com "já existe, use force_submit" para toda página
        // já vinculada, mesmo que o conteúdo precisasse ser corrigido.
        mutationFn: (pageIds: string[]) => addPagesToGroup(groupId as number, pageIds, true),
        onSuccess: (data) => {
            const total = data.results.length;
            const successCount = data.results.filter((r) => r.success).length;
            toast({
                title:
                    successCount === total
                        ? `Template enviado para ${total} nova${total > 1 ? 's' : ''} página${total > 1 ? 's' : ''}.`
                        : `${successCount} de ${total} páginas enviadas com sucesso.`,
                variant: successCount === 0 ? 'destructive' : 'default',
            });
            queryClient.invalidateQueries({ queryKey: ['utility-template-group', groupId] });
            queryClient.invalidateQueries({ queryKey: ['utility-template-groups'] });
            setAddPagesDialogOpen(false);
        },
        onError: () => {
            toast({ title: 'Erro ao adicionar páginas ao template.', variant: 'destructive' });
        },
    });

    const { mutate: retryTemplates, isPending: retrying } = useMutation({
        mutationFn: (templateIds: number[]) => retryUtilityTemplates(templateIds),
        onSuccess: (data) => {
            const skippedCount = data.skipped.length;
            const skippedDetail =
                skippedCount > 0
                    ? `, ${skippedCount} pulada${skippedCount > 1 ? 's' : ''}: ${data.skipped
                          .map((s) => s.reason)
                          .join('; ')}`
                    : '';
            toast({
                title: `${data.reset_count} reenviada${data.reset_count !== 1 ? 's' : ''}${skippedDetail}`,
                variant: data.reset_count === 0 ? 'destructive' : 'default',
            });
            setSelectedFailedIds(new Set());
            queryClient.invalidateQueries({ queryKey: ['utility-template-group', groupId] });
            queryClient.invalidateQueries({ queryKey: ['utility-template-groups'] });
        },
        onError: () => {
            toast({ title: 'Erro ao retentar templates selecionados.', variant: 'destructive' });
        },
    });

    const handleToggleFailedRow = (id: number) => {
        setSelectedFailedIds((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    // Páginas 'failed' ou 'rejected' têm checkbox (ver SubmissionRow,
    // canRetry) — "selecionar todas" considera as duas, dentro do filtro de
    // status/categoria/idioma atualmente aplicado (evita selecionar linhas
    // que nem aparecem na tela).
    const selectableFailedIds = useMemo(
        () => filteredSubmissions.filter((s) => s.status === 'failed' || s.status === 'rejected').map((s) => s.id),
        [filteredSubmissions]
    );
    const allFailedSelected =
        selectableFailedIds.length > 0 && selectableFailedIds.every((id) => selectedFailedIds.has(id));

    const handleToggleSelectAllFailed = () => {
        setSelectedFailedIds((prev) => {
            if (allFailedSelected) return new Set();
            return new Set([...prev, ...selectableFailedIds]);
        });
    };

    const handleRetrySelected = () => {
        if (selectedFailedIds.size === 0) return;
        retryTemplates(Array.from(selectedFailedIds));
    };

    const handleClose = () => {
        setAddPagesDialogOpen(false);
        setSelectedFailedIds(new Set());
        setSearchTerm('');
        setSelectedStatus('all');
        setSelectedCategory('all');
        setSelectedLanguage('all');
        setShowOnlyVariants(false);
        setPreviewLanguage('original');
        onOpenChange(false);
    };

    const renderContent = () => {
        if (isLoading) {
            return (
                <div className="flex justify-center py-16">
                    <Loader2 className="h-6 w-6 animate-spin text-cyan-500" />
                </div>
            );
        }

        if (isError) {
            const isPermissionError = (error as any)?.response?.status === 404;
            if (isPermissionError) {
                return (
                    <ErrorState title="Acesso Restrito">
                        <p>Você não pode ver os detalhes de um template que não pertence a você.</p>
                        <p className="pt-2">
                            Se desejar, feche este modal e use a função de duplicar no card para usar esta
                            estrutura em suas próprias páginas.
                        </p>
                    </ErrorState>
                );
            }
            return (
                <ErrorState title="Erro ao Carregar">
                    <p>Não foi possível carregar os detalhes do template. Tente novamente mais tarde.</p>
                </ErrorState>
            );
        }

        if (!group) {
            return (
                <ErrorState title="Template não encontrado">
                    <p>O template que você está tentando ver não foi encontrado.</p>
                </ErrorState>
            );
        }

        return (
            <div className="flex flex-col space-y-2.5 min-h-0">
                {/* Preview do Messenger, empilhado no topo, centralizado. Select
                    de idioma troca entre a copy-fonte e o translated_content
                    persistido de cada idioma (o que de fato foi/será enviado
                    à Meta naquele idioma, não uma retradução ao vivo). */}
                <div className="max-w-[300px] mx-auto shrink-0 space-y-1.5">
                    {previewLanguageOptions.length > 1 && (
                        <SearchableSelect
                            value={previewLanguage}
                            onValueChange={setPreviewLanguage}
                            options={previewLanguageOptions}
                            placeholder="Idioma do preview"
                            className="h-8 text-xs"
                        />
                    )}
                    <div className="rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                        <UtilityTemplateMessengerPreview
                            primaryTemplate={
                                group.image_url || previewContent.title || previewContent.subtitle
                                    ? {
                                          title: previewContent.title,
                                          subtitle: previewContent.subtitle,
                                          template_name: group.template_name,
                                          image_url: group.image_url,
                                          thumbnail_url: group.thumbnail_url,
                                          buttons: previewContent.buttons,
                                      }
                                    : null
                            }
                            compact
                        />
                    </div>
                </div>

                {/* KPI tiles — substituem o filtro de Status; clicar seleciona/desmarca. */}
                <div className="grid grid-cols-5 gap-2 shrink-0">
                    <KpiTile
                        label="Total"
                        value={statusCounts.all}
                        active={selectedStatus === 'all'}
                        onClick={() => handleKpiClick('all')}
                    />
                    <KpiTile
                        label="Aprovadas"
                        value={statusCounts.approved}
                        active={selectedStatus === 'approved'}
                        onClick={() => handleKpiClick('approved')}
                        activeClass="border-cyan-400 bg-cyan-50 text-cyan-700"
                    />
                    <KpiTile
                        label="Pendentes"
                        value={statusCounts.pending}
                        active={selectedStatus === 'pending'}
                        onClick={() => handleKpiClick('pending')}
                        activeClass="border-amber-400 bg-amber-50 text-amber-700"
                    />
                    <KpiTile
                        label="Rejeitadas"
                        value={statusCounts.rejected}
                        active={selectedStatus === 'rejected'}
                        onClick={() => handleKpiClick('rejected')}
                        activeClass="border-red-400 bg-red-50 text-red-700"
                    />
                    <KpiTile
                        label="Falhas"
                        value={statusCounts.failed}
                        active={selectedStatus === 'failed'}
                        onClick={() => handleKpiClick('failed')}
                        activeClass="border-red-400 bg-red-50 text-red-700"
                    />
                </div>

                <div className="flex items-center justify-between border-b border-gray-100 pb-2.5 shrink-0">
                    <p className="text-sm font-semibold text-gray-900">
                        Páginas <span className="text-gray-400 font-normal">({filteredSubmissions.length})</span>
                    </p>
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setAddPagesDialogOpen(true)}
                    >
                        <Plus className="h-3.5 w-3.5 mr-1.5" />
                        Adicionar páginas
                    </Button>
                </div>

                {/* Barra de filtro — mesmo padrão de Conexões */}
                <div className="bg-gray-50 rounded-lg p-2.5 border border-gray-200 flex flex-wrap items-end gap-2 shrink-0">
                    <div className="flex-1 min-w-32 space-y-1">
                        <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider ml-1">
                            Buscar
                        </label>
                        <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                            <Input
                                placeholder="Página..."
                                className="pl-8 h-8 text-sm"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="flex-1 min-w-28 space-y-1">
                        <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider ml-1">
                            Categoria
                        </label>
                        <SearchableSelect
                            value={selectedCategory}
                            onValueChange={setSelectedCategory}
                            options={[{ value: 'all', label: 'Todas' }, ...categoryOptions]}
                            placeholder="Categoria"
                            className="h-8 text-sm"
                        />
                    </div>
                    <div className="flex-1 min-w-28 space-y-1">
                        <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider ml-1">
                            Idioma
                        </label>
                        <SearchableSelect
                            value={selectedLanguage}
                            onValueChange={setSelectedLanguage}
                            options={[{ value: 'all', label: 'Todos' }, ...languageOptions]}
                            placeholder="Idioma"
                            className="h-8 text-sm"
                        />
                    </div>
                    <div className="flex items-end pb-1.5">
                        <button
                            type="button"
                            onClick={() => setShowOnlyVariants((v) => !v)}
                            aria-pressed={showOnlyVariants}
                            title="Mostrar só páginas cuja submissão é uma variante gerada por retry (_v2, _v3...)"
                            className={`inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg text-xs font-medium border transition-colors ${
                                showOnlyVariants
                                    ? 'bg-amber-50 border-amber-200 text-amber-700'
                                    : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-700'
                            }`}
                        >
                            <GitBranch className="h-3.5 w-3.5" />
                            Só variantes
                        </button>
                    </div>
                </div>

                {/* Barra de ação — retentar templates com falha selecionados.
                    Sticky no topo da lista, com transição de entrada. */}
                <div
                    className={`shrink-0 overflow-hidden transition-all duration-200 ease-out ${
                        selectedFailedIds.size > 0 ? 'max-h-16 opacity-100' : 'max-h-0 opacity-0'
                    }`}
                >
                    <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2.5 flex items-center justify-between gap-3 shadow-sm">
                        <span className="text-sm font-medium text-blue-800">
                            {selectedFailedIds.size} selecionada{selectedFailedIds.size > 1 ? 's' : ''}
                        </span>
                        <div className="flex items-center gap-2">
                            <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                className="h-8 text-gray-600"
                                onClick={() => setSelectedFailedIds(new Set())}
                                disabled={retrying}
                            >
                                Cancelar
                            </Button>
                            <Button
                                type="button"
                                size="sm"
                                className="h-8 bg-blue-600 hover:bg-blue-700 text-white"
                                onClick={handleRetrySelected}
                                disabled={retrying}
                            >
                                {retrying && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                                <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                                Retentar {selectedFailedIds.size} selecionada{selectedFailedIds.size > 1 ? 's' : ''}
                            </Button>
                        </div>
                    </div>
                </div>

                {selectableFailedIds.length > 0 && (
                    <div className="shrink-0 flex items-center gap-2 px-1 pb-1.5">
                        <Checkbox
                            checked={allFailedSelected}
                            onCheckedChange={handleToggleSelectAllFailed}
                            aria-label="Selecionar todas as falhas e rejeições"
                        />
                        <button
                            type="button"
                            onClick={handleToggleSelectAllFailed}
                            className="text-xs font-medium text-gray-500 hover:text-gray-700"
                        >
                            {allFailedSelected
                                ? 'Desmarcar todas'
                                : `Selecionar todas as falhas/rejeições (${selectableFailedIds.length})`}
                        </button>
                    </div>
                )}

                <div className="flex-1 min-h-0 max-h-[50vh] overflow-y-auto pr-1">
                    <div className="flex flex-col gap-1.5">
                        {filteredSubmissions.length === 0 ? (
                            <p className="text-sm text-gray-400 text-center py-6">
                                Nenhuma página encontrada com esse filtro
                            </p>
                        ) : (
                            filteredSubmissions.map((s) => (
                                <SubmissionRow
                                    key={s.id}
                                    submission={s}
                                    page={pageById.get(s.page_id)}
                                    selected={selectedFailedIds.has(s.id)}
                                    onToggleSelect={handleToggleFailedRow}
                                    onCompareWithMeta={() => setComparePageId(s.page_id)}
                                />
                            ))
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            <Dialog open={groupId !== null} onOpenChange={handleClose}>
                <DialogContent className="sm:max-w-4xl max-h-[88vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{group?.template_name ?? 'Detalhes do Template'}</DialogTitle>
                    </DialogHeader>

                    {renderContent()}

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={handleClose}>
                            Fechar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AddPagesDialog
                open={addPagesDialogOpen}
                onOpenChange={setAddPagesDialogOpen}
                pages={pages}
                submittedPageIds={submittedPageIds}
                onSubmit={addPages}
                isPending={adding}
            />

            <PageMetaTemplatesCompareDialog
                pageId={comparePageId}
                pageName={comparePageId ? pageById.get(comparePageId)?.page_name : undefined}
                onOpenChange={(open) => !open && setComparePageId(null)}
            />
        </>
    );
}

interface KpiTileProps {
    label: string;
    value: number;
    active: boolean;
    onClick: () => void;
    activeClass?: string;
}

/**
 * Tile compacto de KPI clicável — substitui o dropdown de Status. Discreto por
 * design: número grande + label pequeno, sem sombra/ícone, coerente com os
 * filtros h-8 e badges pequenos do resto do modal.
 */
function KpiTile({ label, value, active, onClick, activeClass }: KpiTileProps) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`rounded-lg border px-2 py-1.5 text-center transition-colors ${
                active
                    ? activeClass ?? 'border-gray-400 bg-gray-100 text-gray-900'
                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
            }`}
        >
            <p className="text-base font-semibold leading-none">{value}</p>
            <p className="text-[10px] font-medium uppercase tracking-wider text-gray-500 mt-1">{label}</p>
        </button>
    );
}

interface SubmissionRowProps {
    submission: UtilityTemplate;
    page: MetaPage | undefined;
    selected: boolean;
    onToggleSelect: (id: number) => void;
    onCompareWithMeta: () => void;
}

/**
 * Formata submission.last_error para exibição: prioriza error_user_msg (a
 * mensagem legível que a própria Meta devolve, ex: "O nome do modelo já
 * existe."), cai para meta_error.message (formato antigo/mais genérico),
 * depois message/error (exceção sem resposta HTTP da Meta), e por último um
 * aviso de erro desconhecido.
 */
function formatFailedError(lastError: UtilityTemplate['last_error']): { message: string; code?: number } {
    if (lastError?.meta_error?.error_user_msg) {
        return { message: lastError.meta_error.error_user_msg, code: lastError.meta_error.code };
    }
    if (lastError?.meta_error?.message) {
        return { message: lastError.meta_error.message, code: lastError.meta_error.code };
    }
    if (lastError?.message) {
        return { message: lastError.message };
    }
    if (lastError?.error) {
        return { message: lastError.error };
    }
    return { message: 'Erro desconhecido — ver logs' };
}

/**
 * Linha de página no padrão de PagesList (Conexões): ícone + nome + link
 * externo + ID + conta. Erro de rejeição/falha fica sempre visível abaixo
 * da linha (não depende de hover) — uso repetido exige leitura direta.
 */
function SubmissionRow({ submission, page, selected, onToggleSelect, onCompareWithMeta }: SubmissionRowProps) {
    const badgeCls = STATUS_BADGE_CLASS[submission.status] ?? 'bg-gray-100 text-gray-600 border-gray-200';
    const stripeCls = STATUS_STRIPE_CLASS[submission.status] ?? 'border-l-gray-200';
    const isFailed = submission.status === 'failed';
    const isRejected = submission.status === 'rejected';
    // Retry manual aceita 'failed' (erro do nosso pipeline) e 'rejected'
    // (decisão de conteúdo da Meta — retry força retradução, ver
    // retry_failed_templates no backend).
    const canRetry = isFailed || isRejected;
    const showRejection = isRejected && submission.rejection_reason;
    const failedError = isFailed ? formatFailedError(submission.last_error) : null;
    const hasTechnicalDetail = !!(
        submission.original_payload || submission.translated_content || submission.meta_payload
    );
    const retryOrigin = submission.translated_content?.retry_origin;

    return (
        <div
            className={`rounded-lg border-y border-r border-l-4 px-3 py-2.5 hover:bg-gray-50 transition-colors ${stripeCls} ${
                selected ? 'bg-blue-50/40 border-y-blue-200 border-r-blue-200' : 'border-y-gray-100 border-r-gray-100'
            }`}
        >
            <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                    {canRetry && (
                        <Checkbox
                            checked={selected}
                            onCheckedChange={() => onToggleSelect(submission.id)}
                            aria-label={`Selecionar ${page?.page_name ?? submission.page_id}`}
                        />
                    )}
                    <div className="h-8 w-8 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                        <Facebook className="h-3.5 w-3.5 text-blue-600" />
                    </div>
                    <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                            <p className="text-[15px] font-semibold text-gray-900 leading-none truncate">
                                {page?.page_name ?? submission.page_id}
                            </p>
                            <a
                                href={`https://facebook.com/${submission.page_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-gray-400 hover:text-blue-600 shrink-0"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <ExternalLink className="h-3 w-3" />
                            </a>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">
                            ID: {submission.page_id}
                            <span className="text-gray-300 mx-1">·</span>
                            {submission.language}
                        </p>
                        {page?.meta_user_name && (
                            <p className="text-xs text-gray-400">{page.meta_user_name}</p>
                        )}
                    </div>
                </div>
                <div className="shrink-0 flex items-center gap-1.5">
                    {retryOrigin && (
                        <span
                            title={
                                `Variante gerada por retry de "${retryOrigin.previous_template_name ?? '—'}"` +
                                (retryOrigin.rejection_reason_at_retry
                                    ? ` — motivo da rejeição: ${retryOrigin.rejection_reason_at_retry}` +
                                      (retryOrigin.specific_rejection_reason_at_retry
                                          ? ` (${retryOrigin.specific_rejection_reason_at_retry})`
                                          : '')
                                    : '')
                            }
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border border-amber-200 bg-amber-50 text-amber-700"
                        >
                            <GitBranch className="h-3 w-3" />
                            Variante de retry
                        </span>
                    )}
                    <button
                        type="button"
                        onClick={onCompareWithMeta}
                        title="Comparar templates desta página com a Meta"
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                        <Scale className="h-3 w-3" />
                        Comparar com a Meta
                    </button>
                    <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${badgeCls}`}
                    >
                        {STATUS_LABEL[submission.status] ?? submission.status}
                    </span>
                </div>
            </div>
            {showRejection && (
                <div className="flex items-start gap-1.5 text-xs text-red-700 bg-red-50 border border-red-100 rounded-lg px-2 py-1.5 mt-2">
                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-red-500" />
                    <div className="leading-snug">
                        <p>{submission.rejection_reason}</p>
                        {submission.specific_rejection_reason && (
                            <p className="text-red-500 mt-0.5">{submission.specific_rejection_reason}</p>
                        )}
                    </div>
                </div>
            )}
            {failedError && (
                <div className="flex items-start gap-1.5 text-xs text-red-700 bg-red-50 border border-red-100 rounded-lg px-2 py-1.5 mt-2">
                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-red-500" />
                    <p className="leading-snug">
                        {failedError.message}
                        {failedError.code !== undefined && (
                            <span className="text-red-500"> (código {failedError.code})</span>
                        )}
                        {/* Reprocessamento automático (fila utility_retry, até 3 tentativas) —
                            distinto de rejeição da Meta, que não é retentada. */}
                        {typeof submission.retry_count === 'number' && submission.retry_count > 0 && (
                            <span className="text-red-500">
                                {' '}— tentativa {Math.min(submission.retry_count, 3)} de 3
                            </span>
                        )}
                    </p>
                </div>
            )}
            {hasTechnicalDetail && (
                <details className="mt-2 group" onClick={(e) => e.stopPropagation()}>
                    <summary className="cursor-pointer text-[11px] font-medium text-gray-400 hover:text-gray-600 select-none">
                        Ver detalhes técnicos (original, tradução e payload enviado)
                    </summary>
                    <div className="mt-1.5 space-y-1.5">
                        {submission.original_payload && (
                            <TechnicalDetailBlock label="Original (português)">
                                <p><span className="text-gray-400">Título:</span> {submission.original_payload.title || '—'}</p>
                                <p><span className="text-gray-400">Subtítulo:</span> {submission.original_payload.subtitle || '—'}</p>
                                {(submission.original_payload.buttons ?? []).map((btn, i) => (
                                    <p key={i}><span className="text-gray-400">Botão {i + 1}:</span> {btn.text}</p>
                                ))}
                            </TechnicalDetailBlock>
                        )}
                        {submission.translated_content?.translated && (
                            <TechnicalDetailBlock label={`Traduzido pela IA (${submission.language})`}>
                                <p><span className="text-gray-400">Título:</span> {submission.translated_content.translated.title || '—'}</p>
                                <p><span className="text-gray-400">Subtítulo:</span> {submission.translated_content.translated.subtitle || '—'}</p>
                                {(submission.translated_content.translated.buttons ?? []).map((btn, i) => (
                                    <p key={i}><span className="text-gray-400">Botão {i + 1}:</span> {btn.text}</p>
                                ))}
                            </TechnicalDetailBlock>
                        )}
                        {(submission.translated_content?.shortened_fields ?? []).length > 0 && (
                            <TechnicalDetailBlock label="Encurtamento por limite de caracteres da Meta" tone="amber">
                                {submission.translated_content!.shortened_fields!.map((s, i) => (
                                    <div key={i} className="mb-1 last:mb-0">
                                        <p className="font-medium text-amber-800">
                                            {s.field} {s.used_fallback_truncation ? '(cortado direto — IA não resolveu)' : '(reescrito pela IA)'}
                                        </p>
                                        <p><span className="text-gray-400">Original:</span> {s.original}</p>
                                        {s.ai_shortened && (
                                            <p><span className="text-gray-400">Resultado:</span> {s.ai_shortened}</p>
                                        )}
                                    </div>
                                ))}
                            </TechnicalDetailBlock>
                        )}
                        {submission.meta_payload && (
                            <TechnicalDetailBlock label="Payload enviado à Meta (JSON)">
                                <pre className="whitespace-pre-wrap break-all font-mono text-[10px] leading-snug">
                                    {JSON.stringify(submission.meta_payload, null, 2)}
                                </pre>
                            </TechnicalDetailBlock>
                        )}
                    </div>
                </details>
            )}
        </div>
    );
}

function TechnicalDetailBlock({
    label,
    tone = 'gray',
    children,
}: {
    label: string;
    tone?: 'gray' | 'amber';
    children: React.ReactNode;
}) {
    const toneCls = tone === 'amber'
        ? 'bg-amber-50 border-amber-100 text-amber-900'
        : 'bg-gray-50 border-gray-100 text-gray-700';
    return (
        <div className={`rounded-lg border px-2.5 py-2 text-xs leading-snug ${toneCls}`}>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1">{label}</p>
            {children}
        </div>
    );
}
